
import { useState, useEffect } from 'react';
import type { Role, RoleFormData, Permission } from '../types';
import { permissionTree } from '../mockData';

interface RoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: RoleFormData) => void;
  role?: Role;
}

const actionLabels: Record<string, string> = {
  view: '查看',
  add: '新增',
  edit: '编辑',
  delete: '删除',
  export: '导出',
};

export default function RoleModal({ isOpen, onClose, onSave, role }: RoleModalProps) {
  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    description: '',
    permissions: [],
    status: true,
  });

  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);

  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name,
        description: role.description,
        permissions: role.permissions,
        status: role.status,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        permissions: [],
        status: true,
      });
    }
  }, [role, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('请输入角色名称');
      return;
    }
    onSave(formData);
  };

  const toggleNode = (nodeId: string) => {
    setExpandedNodes((prev) =>
      prev.includes(nodeId) ? prev.filter((id) => id !== nodeId) : [...prev, nodeId]
    );
  };

  const isPermissionChecked = (permissionId: string) => {
    return formData.permissions.includes(permissionId);
  };

  const togglePermission = (permissionId: string) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter((p) => p !== permissionId)
        : [...prev.permissions, permissionId],
    }));
  };

  const isNodeFullyChecked = (node: Permission): boolean => {
    if (node.actions) {
      return node.actions.every((action) => formData.permissions.includes(`${node.id}:${action}`));
    }
    if (node.children) {
      return node.children.every((child) => isNodeFullyChecked(child));
    }
    return formData.permissions.includes(node.id);
  };

  const isNodePartiallyChecked = (node: Permission): boolean => {
    if (node.actions) {
      const checkedCount = node.actions.filter((action) =>
        formData.permissions.includes(`${node.id}:${action}`)
      ).length;
      return checkedCount > 0 && checkedCount < node.actions.length;
    }
    if (node.children) {
      return node.children.some((child) => isNodeFullyChecked(child) || isNodePartiallyChecked(child));
    }
    return false;
  };

  const toggleNode_Permission = (node: Permission) => {
    const isChecked = isNodeFullyChecked(node);
    const newPermissions = [...formData.permissions];

    const updatePermissions = (n: Permission, checked: boolean) => {
      if (n.actions) {
        n.actions.forEach((action) => {
          const permId = `${n.id}:${action}`;
          if (checked) {
            if (!newPermissions.includes(permId)) {
              newPermissions.push(permId);
            }
          } else {
            const index = newPermissions.indexOf(permId);
            if (index > -1) {
              newPermissions.splice(index, 1);
            }
          }
        });
      } else if (n.children) {
        n.children.forEach((child) => updatePermissions(child, checked));
      } else {
        if (checked) {
          if (!newPermissions.includes(n.id)) {
            newPermissions.push(n.id);
          }
        } else {
          const index = newPermissions.indexOf(n.id);
          if (index > -1) {
            newPermissions.splice(index, 1);
          }
        }
      }
    };

    updatePermissions(node, !isChecked);
    setFormData((prev) => ({ ...prev, permissions: newPermissions }));
  };

  const renderPermissionTree = (nodes: Permission[], level: number = 0) => {
    return nodes.map((node) => {
      const hasChildren = node.children && node.children.length > 0;
      const isExpanded = expandedNodes.includes(node.id);
      const isFullyChecked = isNodeFullyChecked(node);
      const isPartiallyChecked = isNodePartiallyChecked(node);

      return (
        <div key={node.id} className="mb-2">
          <div className={`flex items-center gap-2 py-1.5 ${level > 0 ? 'ml-6' : ''}`}>
            {hasChildren && (
              <button
                type="button"
                onClick={() => toggleNode(node.id)}
                className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line text-sm`}></i>
              </button>
            )}
            {!hasChildren && <div className="w-5"></div>}
            
            <label className="flex items-center gap-2 cursor-pointer flex-1">
              <div className="relative w-4 h-4 flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={isFullyChecked}
                  onChange={() => toggleNode_Permission(node)}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                />
                {isPartiallyChecked && !isFullyChecked && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-2 h-2 bg-teal-600 rounded-sm"></div>
                  </div>
                )}
              </div>
              <span className="text-sm text-gray-700">{node.name}</span>
            </label>

            {node.actions && (
              <div className="flex items-center gap-2">
                {node.actions.map((action) => {
                  const permId = `${node.id}:${action}`;
                  return (
                    <label key={action} className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isPermissionChecked(permId)}
                        onChange={() => togglePermission(permId)}
                        className="w-3.5 h-3.5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="text-xs text-gray-600">{actionLabels[action]}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {hasChildren && isExpanded && (
            <div className="ml-2">{renderPermissionTree(node.children!, level + 1)}</div>
          )}
        </div>
      );
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">
            {role ? '编辑角色' : '创建角色'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                角色名称 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="请输入角色名称"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">角色描述</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                placeholder="请输入角色描述"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">权限配置</label>
              <div className="border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto bg-gray-50">
                {renderPermissionTree(permissionTree)}
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                />
                <span className="text-sm text-gray-700">启用该角色</span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              取消
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              保存
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
